import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';

export interface AdminNotification {
  type: string;
  id?: number | string;
  ts?: number;
  title?: string;
  message?: string;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class SocketService implements OnDestroy {
  private readonly listenerWrappers = new WeakMap<(...args: any[]) => void, (...args: any[]) => void>();
  private readonly maxNotifications = 50;
  private readonly storageKey = 'cmsNotifications';
  private connectedToken?: string;
  private coreListenersRegistered = false;

  public socket: Socket;

  notifications$ = new BehaviorSubject<AdminNotification[]>(this.loadNotifications());
  notificationReceived$ = new Subject<AdminNotification>();
  unreadCount$ = new BehaviorSubject<number>(0);
  statChanged$ = new Subject<any>();
  driversOnline$ = new BehaviorSubject<number>(0);
  supportMessage$ = new Subject<any>();
  driverLocationUpdated$ = new Subject<any>();

  constructor(
    private router: Router,
    private ngZone: NgZone
  ) {
    this.connect();
  }

  public on(event: string, callback: (...args: any[]) => void): void {
    this.ensureSocket();
    let wrapper = this.listenerWrappers.get(callback);
    if (!wrapper) {
      wrapper = (...args: any[]) => {
        this.ngZone.run(() => callback(...args));
      };
      this.listenerWrappers.set(callback, wrapper);
    }
    this.socket.on(event, wrapper);
  }

  public off(event: string, callback?: (...args: any[]) => void): void {
    if (!this.socket) {
      return;
    }
    if (!callback) {
      this.socket.off(event);
      return;
    }
    const wrapper = this.listenerWrappers.get(callback);
    if (wrapper) {
      this.socket.off(event, wrapper);
    }
  }

  public fromEvent<T = any>(eventName: string): Observable<T> {
    return new Observable<T>((subscriber) => {
      this.ensureSocket();
      const listener = (data: T) => {
        this.ngZone.run(() => subscriber.next(data));
      };
      this.socket.on(eventName, listener);

      return () => {
        if (this.socket) {
          this.socket.off(eventName, listener);
        }
      };
    });
  }

  public emit(event: string, ...args: any[]): void {
    this.ensureSocket();
    const last = args[args.length - 1];
    if (typeof last === 'function') {
      const callback = last as (...cbArgs: any[]) => void;
      args = [
        ...args.slice(0, -1),
        (...cbArgs: any[]) => {
          this.ngZone.run(() => callback(...cbArgs));
        }
      ];
    }
    this.socket.emit(event, ...args);
  }

  public connect(token = localStorage.getItem('token') || ''): void {
    if (!token) {
      return;
    }

    if (this.socket && this.connectedToken === token) {
      if (!this.socket.connected) {
        this.socket.connect();
      }
      return;
    }

    this.disconnect(false);
    this.connectedToken = token;
    this.coreListenersRegistered = false;
    this.socket = io(this.cmsNamespaceUrl(), {
      query: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    this.registerCoreListeners();
  }

  public disconnect(clearToken = true): void {
    if (!this.socket) {
      if (clearToken) {
        this.connectedToken = undefined;
      }
      return;
    }
    this.socket.off('connect');
    this.socket.off('disconnect');
    this.socket.off('connect_error');
    this.socket.off('error');
    this.socket.off('newNotification');
    this.socket.off('statChanged');
    this.socket.off('supportMessageReceived');
    this.socket.off('driversOnline');
    this.socket.off('driverLocationUpdated');
    this.socket.disconnect();
    this.coreListenersRegistered = false;
    if (clearToken) {
      this.connectedToken = undefined;
    }
  }

  get ioSocket(): Socket {
    this.ensureSocket();
    return this.socket;
  }

  markAllRead(): void {
    this.unreadCount$.next(0);
  }

  clearNotifications(): void {
    this.notifications$.next([]);
    this.unreadCount$.next(0);
    localStorage.removeItem(this.storageKey);
  }

  ngOnDestroy(): void {
    this.disconnect();
  }

  applyStatChange<T extends { [key: string]: any }>(stats: T, payload: any): T {
    if (!stats || !payload || typeof payload !== 'object') {
      return stats;
    }

    const key = this.statKey(payload);
    if (!key) {
      return stats;
    }

    const statMap = stats as any;
    const current = Number(statMap[key] || 0);
    const delta = payload.delta != null ? Number(payload.delta) : payload.value != null ? Number(payload.value) : 1;
    statMap[key] = Math.max(0, current + (Number.isFinite(delta) ? delta : 0));
    return stats;
  }

  private ensureSocket(): void {
    const token = localStorage.getItem('token') || '';
    if (!this.socket || (token && this.connectedToken !== token)) {
      this.connect(token);
    }
  }

  private registerCoreListeners(): void {
    if (!this.socket || this.coreListenersRegistered) {
      return;
    }
    this.coreListenersRegistered = true;

    this.socket.on('connect', () => {
      console.log('[CMS socket] connected', this.socket?.id);
    });

    this.socket.on('disconnect', (reason: string) => {
      this.handleDisconnection(reason);
    });

    this.socket.on('connect_error', (error: any) => {
      console.error('[CMS socket] connect error', error?.message || error);
      this.handleUnauthorized(error);
    });

    this.socket.on('error', (error: any) => {
      this.handleUnauthorized(error);
    });

    this.socket.on('newNotification', (payload: AdminNotification) => {
      this.ngZone.run(() => this.addNotification(this.normalizeNotification(payload)));
    });

    this.socket.on('statChanged', (payload: any) => {
      this.ngZone.run(() => this.statChanged$.next(payload));
    });

    this.socket.on('supportMessageReceived', (payload: any) => {
      this.ngZone.run(() => {
        this.supportMessage$.next(payload);
        this.addNotification(this.normalizeNotification({
          type: 'support',
          id: payload?.id ?? payload?.messageId ?? payload?.message_id,
          ts: Date.now(),
          message: payload?.content || payload?.message || 'New support message',
          raw: payload,
        }));
      });
    });

    this.socket.on('driversOnline', (payload: any) => {
      this.ngZone.run(() => this.driversOnline$.next(Number(payload?.count || 0)));
    });

    this.socket.on('driverLocationUpdated', (payload: any) => {
      this.ngZone.run(() => this.driverLocationUpdated$.next(payload));
    });
  }

  private addNotification(notification: AdminNotification): void {
    const notifications = [notification, ...this.notifications$.value].slice(0, this.maxNotifications);
    this.notifications$.next(notifications);
    this.notificationReceived$.next(notification);
    this.unreadCount$.next(this.unreadCount$.value + 1);
    localStorage.setItem(this.storageKey, JSON.stringify(notifications));
  }

  private loadNotifications(): AdminNotification[] {
    try {
      const raw = localStorage.getItem(this.storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.slice(0, this.maxNotifications) : [];
    } catch (_error) {
      return [];
    }
  }

  private normalizeNotification(payload: AdminNotification = { type: 'notification' }): AdminNotification {
    const type = payload?.type || 'notification';
    return {
      ...payload,
      type,
      ts: payload?.ts || Date.now(),
      title: payload?.title || this.titleForType(type),
      message: payload?.message || this.messageForPayload({ ...payload, type }),
    };
  }

  private titleForType(type: string): string {
    switch (String(type).toLowerCase()) {
      case 'complaint':
        return 'New complaint';
      case 'feedback':
        return 'New feedback';
      case 'dispute':
        return 'New dispute';
      case 'support':
        return 'New support message';
      case 'payment_request':
      case 'paymentrequested':
      case 'paymentrequest':
        return 'New payment request';
      case 'driver':
        return 'Driver submitted';
      default:
        return 'New notification';
    }
  }

  private messageForPayload(payload: AdminNotification): string {
    if (payload?.subject) return String(payload.subject);
    if (payload?.message) return String(payload.message);
    if (payload?.id != null) return `${this.titleForType(payload.type)} #${payload.id}`;
    return this.titleForType(payload.type);
  }

  private statKey(payload: any): string | null {
    const rawKey = payload.key || payload.type;
    if (!rawKey) {
      return null;
    }
    const key = String(rawKey);
    const normalized = key.replace(/[-_\s]/g, '').toLowerCase();
    switch (normalized) {
      case 'complaint':
      case 'complaints':
        return 'complaints';
      case 'feedback':
      case 'feedbacks':
        return 'feedbacks';
      case 'dispute':
      case 'disputes':
      case 'totaldisputes':
        return 'totalDisputes';
      case 'paymentrequest':
      case 'paymentrequests':
      case 'paymentrequested':
      case 'payoutrequest':
      case 'payoutrequests':
        return 'paymentRequests';
      case 'driver':
      case 'driverspending':
      case 'driverpending':
      case 'pendingdrivers':
        return 'driversPending';
      case 'request':
      case 'requests':
        return 'requests';
      case 'availabledrivers':
      case 'driversonline':
        return 'availableDrivers';
      default:
        return key;
    }
  }

  private cmsNamespaceUrl(): string {
    return `${environment.root.replace(/\/+$/, '')}/cms`;
  }

  private handleUnauthorized(error: any): void {
    const text = String(error?.message || error?.type || '').toLowerCase();
    if (error?.type === 'UnauthorizedError' || text.includes('unauthorized') || text.includes('jwt')) {
      localStorage.removeItem('token');
      this.disconnect();
      this.router.navigate(['./login']);
    }
  }

  private handleDisconnection(reason: string): void {
    console.warn('[CMS socket] disconnected. Reason: ' + reason);
  }
}
